/**
 * v-reveal 入场动画指令
 * ------------------------------------------------------------
 * 给容器加上 v-reveal 后，它的直接子元素会在进入视口时
 * 逐个（stagger）播放"收纳展开"入场动画，制造层层弹出的效果。
 *
 * 用法：
 *   <div v-reveal>...子元素...</div>
 *   <div v-reveal="{ stagger: 60, anim: 'unfold', y: 26 }">...</div>
 *
 * 可选参数：
 *   - anim：动画类型，对应 theme.css 里的 .rv-<anim> 关键帧
 *           （unfold / pop / flip / blur / zoom），默认 unfold
 *   - stagger：相邻子元素的延迟毫秒数，默认 55
 *   - y：初始下移像素（部分动画会用到），默认 24
 *   - once：是否只播放一次，默认 true
 *   - duration：单元素动画时长 ms，默认 620
 */

// 给单个容器初始化：标记子元素初始隐藏，进视口后逐个触发
function setup(el, binding) {
    const opt = binding.value || {};
    const anim = opt.anim || "unfold";
    const stagger = Number(opt.stagger ?? 55);
    const y = Number(opt.y ?? 24);
    const once = opt.once !== false;
    const duration = Number(opt.duration ?? 620);

    // 收集直接子元素作为动画目标
    const kids = Array.from(el.children);
    if (!kids.length) return;

    // 先把子元素打到初始态（隐藏 + 轻微位移），加类名由 CSS 控制
    kids.forEach((k) => {
        k.classList.add("rv-item");
        k.style.setProperty("--rv-y", y + "px");
        k.style.setProperty("--rv-dur", duration + "ms");
    });

    let played = false;

    const play = () => {
        if (played && once) return;
        played = true;
        kids.forEach((k, i) => {
            // 每个子元素错开延迟，形成"逐个展开"的节奏；
            // 同时给延迟封顶，避免卡片很多时最后几张等太久
            const delay = Math.min(i * stagger, 900);
            k.style.animationDelay = delay + "ms";
            k.classList.remove("rv-go"); // 重置以便重复触发
            // 强制回流，保证 remove/add 之间动画能重新播放
            void k.offsetWidth;
            k.classList.add(`rv-${anim}`, "rv-go");
        });
    };

    // 终极安全网：无论观察器/兜底是否触发，到点后强制让子元素可见，
    // 避免移动端因过渡动画、合成层缓存导致元素永久卡在 opacity:0。
    const forceShow = () => {
        kids.forEach((k) => {
            k.classList.remove("rv-item");
            k.style.opacity = "";
        });
    };
    let safety = setTimeout(forceShow, 700);

    // play 真正执行时，撤销安全网（动画会接管显示）
    const playSafe = () => {
        clearTimeout(safety);
        play();
    };

    // 用 IntersectionObserver 在元素进入视口时触发
    const io = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    playSafe();
                    if (once) io.unobserve(el);
                }
            });
        },
        { threshold: 0.01 }
    );

    io.observe(el);

    // 兜底：路由切换后元素可能已经在视口内但回调延迟。
    // 用 rAF 等过渡动画稳定一帧后再判断，比单次 setTimeout 可靠。
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            const r = el.getBoundingClientRect();
            if (r.top < window.innerHeight && r.bottom > 0) playSafe();
        });
    });

    // 把清理函数挂到元素上，卸载时断开观察并清掉定时器
    el._rvCleanup = () => {
        io.disconnect();
        clearTimeout(safety);
    };
}

export const reveal = {
    mounted(el, binding) {
        setup(el, binding);
    },
    unmounted(el) {
        if (el._rvCleanup) el._rvCleanup();
    },
};

export default reveal;
